import React from 'react';
import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import { Instagram as InstagramIcon } from '@mui/icons-material';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { isVisible } from '../../corelogic/model/section';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { getCardStyles, getSecondaryButtonStyles, textMeasure } from '../../shared/styles';
import { SEO } from './SEO';
import { BlockRenderer } from './blocks/BlockRenderer';
import { PageSections } from './PageSections';
import { Section, PageHeader, SectionHeading } from './PageSection';
import { InstagramFeed } from './InstagramFeed';

const contentUseCase = new ContentUseCase();

export const InstagramPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const social = contentUseCase.getSocialMediaContent(language);
  const addedBefore = (slot: string) =>
    contentUseCase.getExtraBlocksBefore('socialMedia', slot, language);

  const sections: Record<string, React.ReactNode> = {
    /* The account itself, and the one link that leaves the site */
    profile: isVisible(social.profile) && (
      <Section labelledBy="profile">
        <Paper elevation={0} sx={{ ...getCardStyles(theme), p: { xs: 3, md: 5 }, '&:hover': {} }}>
          <InstagramIcon sx={{ fontSize: 32, color: 'primary.dark', mb: 2 }} />
          <Typography id="profile" variant="h3" component="h2" sx={{ mb: 0.5 }}>
            {social.profile.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {social.instagram.handle}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3.5, ...textMeasure }}>
            {social.profile.description}
          </Typography>
          <Button
            href={social.instagram.url}
            target="_blank"
            rel="noopener noreferrer me"
            sx={getSecondaryButtonStyles(theme)}
            startIcon={<InstagramIcon />}
          >
            {social.page.followButton}
          </Button>
        </Paper>
      </Section>
    ),

    /* The posts, read here rather than on Instagram */
    feed: isVisible(social.feed) && (
      <Section labelledBy="feed">
        <SectionHeading
          id="feed"
          title={social.feed.title}
          description={social.feed.description}
        />
        {/* The card above already carries the link to Instagram, so the feed's
            fallbacks only offer it when that card is switched off. */}
        <InstagramFeed profileLink={!isVisible(social.profile)} />
      </Section>
    ),
  };

  return (
    <Box>
      <SEO page="instagram" />

      <BlockRenderer blocks={addedBefore('header')} />

      <PageHeader title={social.page.title} subtitle={social.page.subtitle} />

      <PageSections
        page="socialMedia"
        sections={sections}
        order={social.sectionOrder}
        addedBefore={addedBefore}
      />
    </Box>
  );
};
